import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import siteMessage from "@insite/client-framework/SiteMessage";
import subscribe from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowLeftIcon from "@tCentro/Icons/ArrowLeftIcon";
import ArrowRightIcon from "@tCentro/Icons/ArrowRightIcon";
import ArrowRightLongIcon from "@tCentro/Icons/ArrowRightLongIcon";
import EnvelopeIcon from "@tCentro/Icons/EnvelopeIcon";
import RightArrowIcon from "@tCentro/Icons/RightArrowIcon";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    description = "description",
    placeholder = "placeholder",
    alignment = "alignment",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.description]: string;
        [fields.placeholder]: string;
        [fields.alignment]: string;
    };
}

const mapDispatchToProps = {
    subscribe,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SubscribeStyles {
    mainGridContainer: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    emailTextFieldSelected?: TextFieldPresentationProps;
    emailButton?: ButtonPresentationProps;
    disclaimerGridItem?: GridItemProps;
    iconStyle?: InjectableCss;
}

export const subscribeStyles: SubscribeStyles = {
    mainGridContainer: {
        gap: 10,
        css: css`
            width: 100%;
            display: flex;
            align-items: center !important;

            > div {
                ${({ theme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                        flex-direction: column;
                        justify:content: center;
                        `,
                        css`
                        flex-direction: column;
                        justify:content: center;
                        `,
                        null,
                        null,
                        null,
                    ])};
            }
            .cont {
                ~ button {
                    display: none;
                }
            }
            .cont2 {
                ~ button {
                    display: block;
                }
            }
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    titleLabel: {
        variant: "h4",
        css: css`
            margin-bottom: -5px;
        `,
    },
    descriptionGridItem: {
        css: css`
            flex-basis: 46%;
            max-width: 46%;
            padding-right: 32px !important;
            padding: 0;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: center;
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                        margin-bottom: 16px !important;
                        padding-right: 0 !important;
                    `,
                    css`
                        justify-content: center;
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                        margin-bottom: 16px !important;
                        padding-right: 0 !important;
                    `,
                    css`
                        justify-content: center;
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                        padding-right: 0 !important;
                        margin-bottom: 0px !important;
                        span {
                            margin-bottom: 16px !important;
                        }
                    `,
                    css`
                        span {
                            margin-top: 9px !important;
                        }
                    `,
                    css`
                        span {
                            margin-top: 9px !important;
                        }
                    `,
                ])};
            @media (min-width: 992px) and (max-width: 1100px) {
                flex-basis: 55%;
                max-width: 55%;
                padding-right: 20px !important;
            }
            p {
                font-size: ${TYPOGRAPHY.size.base};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: 24px;
                color: ${getProp("theme.typography.p.color")};

                ${({ theme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            margin-bottom: 20px !important;
                            justify-content: center;
                            font-size: ${TYPOGRAPHY.size.smaller};
                            line-height: 16px;
                        `,
                        css`
                            margin-bottom: 20px !important;
                            justify-content: center;
                            font-size: ${TYPOGRAPHY.size.smaller};
                            line-height: 16px;
                        `,
                        css`
                            font-size: 14px;
                            line-height: 20px;
                        `,
                        null,
                        null,
                    ])};
            }
        `,
    },
    descriptionText: {
        css: css`
            margin-bottom: -1rem;
        `,
    },
    emailGridItem: {
        css: css`
            flex-basis: 54%;
            max-width: 54%;
            padding: 0;
            position: relative;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: center;
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                    `,
                    css`
                        justify-content: center;
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                    `,
                    css`
                        margin: 0 auto !important;
                        justify-content: center;
                        flex-basis: 65%;
                        max-width: 65%;
                    `,
                    null,
                    null,
                ])};
            @media (min-width: 992px) and (max-width: 1100px) {
                flex-basis: 45%;
                max-width: 45%;
            }
        `,
    },
    emailTextField: {
        iconProps: {
            src: ArrowRightIcon,
            css: css`
                margin-right: 10px;
                svg {
                    width: 18px;
                    height: 11.25px;
                }
            `,
        },
        cssOverrides: {
            formField: css`
                input {
                    border-radius: 40px !important;
                    padding: 12px 24px !important;
                    padding-right: 45px !important;
                    padding-left: 50px !important;
                    &:focus {
                        border: 2px solid ${COLORS.black} !important;
                    }
                }
                input[type="text"] {
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 24px;
                }
                button {
                    &:focus {
                        outline: none;
                    }
                }
            `,
        },
    },
    emailTextFieldSelected: {
        iconProps: {
            src: RightArrowIcon,
            css: css`
                margin-right: 10px;
                svg {
                    width: 18px;
                    height: 11.25px;
                }
            `,
        },
        cssOverrides: {
            formField: css`
                input {
                    border-radius: 20px !important;
                    padding: 12px 24px !important;
                    padding-left: 50px !important;
                    padding-right: 45px !important;
                    border: 2px solid #3ca865 !important;
                }
                input[type="text"] {
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 24px;
                }
                button {
                    &:focus {
                        outline: none;
                    }
                }
            `,
        },
    },
    iconStyle: {
        css: css`
            position: absolute;
            z-index: 1;
            left: 24px;
            top: 14px;
            height: 13.5px;
            width: 18px;
            svg {
                height: 13.5px;
                width: 18px;
            }
        `,
    },
};

const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.(?!usa$)\\w+([-.]\\w+)*", "i");

export const CmsSubscribe = ({ fields, subscribe, id }: Props) => {
    const emailRequiredFieldMessage = siteMessage("EmailSubscription_EmailIsRequiredErrorMessage");
    const emailFieldMessage = siteMessage("EmailSubscription_EmailIsInvalidErrorMessage");

    const [email, setEmail] = useState("");
    const [style, setStyles] = useState("cont");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const styles = useMergeStyles("subscribe", subscribeStyles);

    const onSubscribeClick = () => {
        setIsSubmitted(true);
        if (!validateEmail(email)) {
            return;
        }

        subscribe({
            email,
            onSuccess: () => {
                toasterContext.addToast({ body: siteMessage("Email_Subscribe_Success"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
        setIsSubmitted(false);
    };

    const changeStyle = () => {
        setStyles("cont2");
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const gridItemCss = (gridItemProps?: GridItemProps) => {
        return css`
            justify-content: ${fields.alignment ? fields.alignment : "center"};
            ${gridItemProps?.css}
        `;
    };

    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem {...styles.descriptionGridItem} css={gridItemCss(styles.descriptionGridItem)}>
                <Typography {...styles.descriptionText}>
                    {parse(transformContentHtml(fields.description), parserOptions)}
                </Typography>
            </GridItem>
            <GridItem {...styles.emailGridItem} css={gridItemCss(styles.emailGridItem)}>
                <Icon src={EnvelopeIcon} {...styles.iconStyle}></Icon>
                <TextField
                    className={style}
                    {...(isSubmitted && !emailError ? styles.emailTextFieldSelected : styles.emailTextField)}
                    iconClickableProps={{ onClick: onSubscribeClick }}
                    id={id}
                    placeholder={fields.placeholder}
                    value={email}
                    onChange={e => emailChangeHandler(e.currentTarget.value)}
                    onClick={changeStyle}
                    error={isSubmitted && emailError}
                />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CmsSubscribe),
    definition: {
        group: "Basic",
        icon: "bell",
        fieldDefinitions: [
            {
                name: fields.alignment,
                displayName: "Layout",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                fieldType: "Translatable",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "<p>Keep up-to-date on product news and the latest offers.</p>",
                fieldType: "Translatable",
            },
            {
                name: fields.placeholder,
                displayName: "Placeholder Text",
                editorTemplate: "TextField",
                defaultValue: "Enter email address",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
